import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Edit, Trash2, AlertTriangle, Shield, Activity, TrendingUp, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

export default function ThreatIntelligencePage() {
  const [threats, setThreats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingThreat, setEditingThreat] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [feedStatus, setFeedStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [formData, setFormData] = useState({
    threat_id: '',
    title: '',
    description: '',
    threat_type: 'Malware',
    source: 'Internal Analysis',
    severity: 'Medium',
    likelihood: 'Medium',
    impact: 'Medium',
    status: 'Active',
    affected_systems: '',
    indicators_of_compromise: '',
    cve_ids: '',
    mitigation_actions: '',
    recommendations: '',
    confidence_level: 'Medium',
    intelligence_source_url: '',
    next_review_date: ''
  });

  useEffect(() => {
    fetchThreats();
    fetchStats();
    fetchFeedStatus();
  }, []);

  const fetchThreats = async () => {
    try {
      const response = await axios.get(`${API}/threats`);
      setThreats(response.data);
    } catch (error) {
      toast.error('Failed to fetch threats');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/threats/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch threat stats');
    }
  };

  const fetchFeedStatus = async () => {
    try {
      const response = await axios.get(`${API}/threats/feeds/status`);
      setFeedStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch feed status');
    }
  };

  const handleSyncFeeds = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/threats/feeds/sync`);
      toast.success(`Synced ${response.data.threats_created} new threats from government feeds`);
      fetchThreats();
      fetchStats();
      fetchFeedStatus();
    } catch (error) {
      toast.error('Failed to sync threat feeds');
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        affected_systems: formData.affected_systems ? formData.affected_systems.split(',').map(s => s.trim()) : [],
        indicators_of_compromise: formData.indicators_of_compromise ? formData.indicators_of_compromise.split(',').map(i => i.trim()) : [],
        cve_ids: formData.cve_ids ? formData.cve_ids.split(',').map(c => c.trim()) : []
      };

      if (editingThreat) {
        await axios.patch(`${API}/threats/${editingThreat.id}`, payload);
        toast.success('Threat updated successfully');
      } else {
        await axios.post(`${API}/threats`, payload);
        toast.success('Threat created successfully');
      }
      
      setShowDialog(false);
      setEditingThreat(null);
      resetForm();
      fetchThreats();
      fetchStats();
    } catch (error) {
      toast.error(editingThreat ? 'Failed to update threat' : 'Failed to create threat');
    }
  };

  const handleEdit = (threat) => {
    setEditingThreat(threat);
    setFormData({
      threat_id: threat.threat_id,
      title: threat.title,
      description: threat.description,
      threat_type: threat.threat_type,
      source: threat.source,
      severity: threat.severity,
      likelihood: threat.likelihood,
      impact: threat.impact,
      status: threat.status,
      affected_systems: threat.affected_systems?.join(', ') || '',
      indicators_of_compromise: threat.indicators_of_compromise?.join(', ') || '',
      cve_ids: threat.cve_ids?.join(', ') || '',
      mitigation_actions: threat.mitigation_actions || '',
      recommendations: threat.recommendations || '',
      confidence_level: threat.confidence_level,
      intelligence_source_url: threat.intelligence_source_url || '',
      next_review_date: threat.next_review_date || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (threatId) => {
    if (window.confirm('Are you sure you want to delete this threat?')) {
      try {
        await axios.delete(`${API}/threats/${threatId}`);
        toast.success('Threat deleted successfully');
        fetchThreats();
        fetchStats();
      } catch (error) {
        toast.error('Failed to delete threat');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      threat_id: '',
      title: '',
      description: '',
      threat_type: 'Malware',
      source: 'Internal Analysis',
      severity: 'Medium',
      likelihood: 'Medium',
      impact: 'Medium',
      status: 'Active',
      affected_systems: '',
      indicators_of_compromise: '',
      cve_ids: '',
      mitigation_actions: '',
      recommendations: '',
      confidence_level: 'Medium',
      intelligence_source_url: '',
      next_review_date: ''
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critical': return 'bg-slate-100 text-red-800 border-slate-700';
      case 'High': return 'bg-slate-100 text-slate-800 border-slate-700';
      case 'Medium': return 'bg-slate-100 text-slate-800 border-slate-700';
      case 'Low': return 'bg-slate-100 text-green-800 border-slate-700';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-slate-100 text-red-800';
      case 'Monitoring': return 'bg-slate-100 text-slate-800';
      case 'Mitigated': return 'bg-slate-100 text-green-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredThreats = threats.filter(threat => {
    if (filterSeverity !== 'all' && threat.severity !== filterSeverity) return false;
    if (filterStatus !== 'all' && threat.status !== filterStatus) return false;
    return true;
  });

  const COLORS = {
    Critical: '#ef4444',
    High: '#f97316',
    Medium: '#eab308',
    Low: '#22c55e'
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold">Threat Intelligence</h1>
          <p className="text-gray-700 mt-1">ISO 27001 A.5.7 - Threat Intelligence Management • Showing Active Threats (Last 7 Days)</p>
        </div>
      </div>

      {/* Hidden Dialog for Edit Only */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>View Threat Details</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Threat ID</Label>
                  <Input
                    value={formData.threat_id}
                    onChange={(e) => setFormData({ ...formData, threat_id: e.target.value })}
                    placeholder="THR-001"
                    required
                    disabled
                  />
                </div>
                <div>
                  <Label>Threat Type</Label>
                  <select
                    value={formData.threat_type}
                    onChange={(e) => setFormData({ ...formData, threat_type: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Malware</option>
                    <option>Phishing</option>
                    <option>DDoS</option>
                    <option>Ransomware</option>
                    <option>Data Breach</option>
                    <option>Insider Threat</option>
                    <option>APT</option>
                    <option>Zero-Day</option>
                    <option>Supply Chain</option>
                    <option>SQL Injection</option>
                    <option>XSS</option>
                  </select>
                </div>
              </div>

              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Threat title"
                  required
                  disabled
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed threat description"
                  rows={3}
                  required
                  disabled
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Source</Label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Internal Analysis</option>
                    <option>OSINT</option>
                    <option>Threat Feed</option>
                    <option>Vendor Advisory</option>
                    <option>Security Community</option>
                    <option>Government Alert</option>
                    <option>CERT</option>
                  </select>
                </div>
                <div>
                  <Label>Confidence Level</Label>
                  <select
                    value={formData.confidence_level}
                    onChange={(e) => setFormData({ ...formData, confidence_level: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Severity</Label>
                  <select
                    value={formData.severity}
                    onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
                <div>
                  <Label>Likelihood</Label>
                  <select
                    value={formData.likelihood}
                    onChange={(e) => setFormData({ ...formData, likelihood: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Very High</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                    <option>Very Low</option>
                  </select>
                </div>
                <div>
                  <Label>Impact</Label>
                  <select
                    value={formData.impact}
                    onChange={(e) => setFormData({ ...formData, impact: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Critical</option>
                    <option>High</option>
                    <option>Medium</option>
                    <option>Low</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-input rounded-sm px-3 py-2 text-sm"
                  >
                    <option>Active</option>
                    <option>Monitoring</option>
                    <option>Mitigated</option>
                    <option>Closed</option>
                  </select>
                </div>
                <div>
                  <Label>Next Review Date</Label>
                  <Input
                    type="date"
                    value={formData.next_review_date}
                    onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                    disabled
                  />
                </div>
              </div>

              <div>
                <Label>Affected Systems (comma-separated)</Label>
                <Input
                  value={formData.affected_systems}
                  onChange={(e) => setFormData({ ...formData, affected_systems: e.target.value })}
                  placeholder="Web Server, Database, Email System"
                  disabled
                />
              </div>

              <div>
                <Label>Indicators of Compromise (comma-separated)</Label>
                <Textarea
                  value={formData.indicators_of_compromise}
                  onChange={(e) => setFormData({ ...formData, indicators_of_compromise: e.target.value })}
                  placeholder="192.168.1.1, malicious-domain.com, file-hash-123"
                  rows={2}
                  disabled
                />
              </div>

              <div>
                <Label>CVE IDs (comma-separated)</Label>
                <Input
                  value={formData.cve_ids}
                  onChange={(e) => setFormData({ ...formData, cve_ids: e.target.value })}
                  placeholder="CVE-2024-1234, CVE-2024-5678"
                  disabled
                />
              </div>

              <div>
                <Label>Mitigation Actions</Label>
                <Textarea
                  value={formData.mitigation_actions}
                  onChange={(e) => setFormData({ ...formData, mitigation_actions: e.target.value })}
                  placeholder="Actions taken or planned to mitigate this threat"
                  rows={2}
                  disabled
                />
              </div>

              <div>
                <Label>Recommendations</Label>
                <Textarea
                  value={formData.recommendations}
                  onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                  placeholder="Recommendations for addressing this threat"
                  rows={2}
                  disabled
                />
              </div>

              <div>
                <Label>Intelligence Source URL</Label>
                <Input
                  type="url"
                  value={formData.intelligence_source_url}
                  onChange={(e) => setFormData({ ...formData, intelligence_source_url: e.target.value })}
                  placeholder="https://source-url.com"
                  disabled
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowDialog(false); setEditingThreat(null); resetForm(); }}>
                  Close
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      {/* ISO 27001 Compliance Banner */}
      <div className="bg-slate-50 border border-slate-700 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-slate-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-slate-900">ISO 27001:2022 Control A.5.7</h3>
            <p className="text-sm text-slate-700 mt-1">
              Information about information security threats shall be collected and analyzed to produce threat intelligence.
              {stats && stats.total > 0 && (
                <span className="ml-2 font-semibold">✓ Active ({stats.total} threats tracked)</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Threat Feed Status */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Government Threat Feeds
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Real-time alerts from ACSC, CERT Australia, and CISA
            </p>
          </div>
          <Button onClick={handleSyncFeeds} disabled={syncing}>
            {syncing ? (
              <>
                <Activity className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <TrendingUp className="w-4 h-4 mr-2" />
                Sync Feeds
              </>
            )}
          </Button>
        </div>

        {feedStatus && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {feedStatus.feeds.map((feed, index) => (
                <div key={index} className="border border-border rounded-sm p-3">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-sm">{feed.name}</h4>
                    <span className={`px-2 py-0.5 rounded-sm text-xs ${feed.enabled ? 'bg-slate-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {feed.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">{feed.country} • {feed.type}</p>
                </div>
              ))}
            </div>
            {feedStatus.last_sync && (
              <div className="text-sm text-slate-600">
                Last synced: {new Date(feedStatus.last_sync).toLocaleString()}
                {feedStatus.last_sync_details && (
                  <span className="ml-2">
                    • {feedStatus.last_sync_details.threats_created} new threats imported
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <>
          <div className="bg-slate-50 border border-slate-700 rounded-sm p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-slate-700" />
              <span className="text-sm font-medium text-slate-900">
                Active Threat Period: Last 7 Days
              </span>
            </div>
            <span className="text-xs text-slate-700">
              {stats.date_range && `${new Date(stats.date_range.from).toLocaleDateString()} - ${new Date(stats.date_range.to).toLocaleDateString()}`}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Threats</p>
                <p className="text-2xl font-bold mt-1">{stats.total}</p>
              </div>
              <Activity className="w-8 h-8 text-slate-600" />
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Active Threats</p>
                <p className="text-2xl font-bold mt-1 text-red-600">{stats.by_status.active}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Monitoring</p>
                <p className="text-2xl font-bold mt-1 text-slate-600">{stats.by_status.monitoring}</p>
              </div>
              <Eye className="w-8 h-8 text-slate-600" />
            </div>
          </div>
          <div className="bg-white border border-border rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Mitigated</p>
                <p className="text-2xl font-bold mt-1 text-green-600">{stats.by_status.mitigated}</p>
              </div>
              <Shield className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
        </>
      )}

      {/* Charts */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-border rounded-sm p-6">
            <h3 className="font-semibold mb-4">Threats by Severity</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Critical', value: stats.by_severity.critical },
                    { name: 'High', value: stats.by_severity.high },
                    { name: 'Medium', value: stats.by_severity.medium },
                    { name: 'Low', value: stats.by_severity.low }
                  ].filter(item => item.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[
                    { name: 'Critical', value: stats.by_severity.critical },
                    { name: 'High', value: stats.by_severity.high },
                    { name: 'Medium', value: stats.by_severity.medium },
                    { name: 'Low', value: stats.by_severity.low }
                  ].filter(item => item.value > 0).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-border rounded-sm p-6">
            <h3 className="font-semibold mb-4">Threat Types Distribution</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.threat_types}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="flex gap-4 items-center">
          <div className="flex-1">
            <Label className="text-sm">Filter by Severity</Label>
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1"
            >
              <option value="all">All Severities</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div className="flex-1">
            <Label className="text-sm">Filter by Status</Label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-input rounded-sm px-3 py-2 text-sm mt-1"
            >
              <option value="all">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Monitoring">Monitoring</option>
              <option value="Mitigated">Mitigated</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="flex-1 flex items-end">
            <p className="text-sm text-slate-600">
              Showing {filteredThreats.length} of {threats.length} threats
            </p>
          </div>
        </div>
      </div>

      {/* Threats Table */}
      <div className="bg-white border border-border rounded-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 text-sm font-semibold">Threat ID</th>
                <th className="text-left p-4 text-sm font-semibold">Title</th>
                <th className="text-left p-4 text-sm font-semibold">Type</th>
                <th className="text-left p-4 text-sm font-semibold">Severity</th>
                <th className="text-left p-4 text-sm font-semibold">Status</th>
                <th className="text-left p-4 text-sm font-semibold">Date Identified</th>
                <th className="text-right p-4 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center p-8 text-slate-600">
                    Loading threats...
                  </td>
                </tr>
              ) : filteredThreats.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-8 text-gray-700">
                    No threats found. Click &quot;Sync Feeds&quot; to import threats from ACSC, CISA, and CERT Australia.
                  </td>
                </tr>
              ) : (
                filteredThreats.map((threat) => (
                  <tr key={threat.id} className="border-b hover:bg-slate-50">
                    <td className="p-4 text-sm font-medium">{threat.threat_id}</td>
                    <td className="p-4 text-sm">{threat.title}</td>
                    <td className="p-4 text-sm">{threat.threat_type}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getSeverityColor(threat.severity)}`}>
                        {threat.severity}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getStatusColor(threat.status)}`}>
                        {threat.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm">{new Date(threat.date_identified).toLocaleDateString()}</td>
                    <td className="p-4">
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(threat)} title="View Details">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
